import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Tooltip, TextField, MenuItem,
    InputAdornment, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PRIMARY } from '../theme';
import { card, ghostBtn, field, EmptyState, LoadAlert } from '../components/uiKit';
import { INK, MUTED, BODY, LINE, LINE_STRONG, HOVER, RADIUS, Label, Segmented } from '../components/LeaveAttendanceComps/punchUi';
import { initialsFromName as initials, paletteColor as colorFor, parseApiDate, fmtClockTime, fmtDayMon, fmtMinutes, avatarTone } from '../utils/format';
import { decodePolyline, routeLengthMetres, fmtDistance } from '../utils/polyline';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import { GetLocationTrips, GetTripRoute, GetClientLocations } from '../Api/Api';

const INDIA_CENTER = [22.5, 79];

const STATUSES = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'abandoned', label: 'Abandoned' },
    { key: 'cancelled', label: 'Cancelled' },
];

const TRIP_STATUS = {
    active: { label: 'Active', color: '#15803D', bg: '#F0FDF4', border: '#C4EBD1' },
    arrived: { label: 'Arrived', color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD' },
    abandoned: { label: 'Abandoned', color: '#B45309', bg: '#FEF6EC', border: '#F6E0C2' },
    cancelled: { label: 'Cancelled', color: '#BE123C', bg: '#FEF3F5', border: '#F8CDD7' },
};
const statusOf = (k) => TRIP_STATUS[String(k || '').toLowerCase()]
    || { label: k || '—', color: '#64748B', bg: '#F5F7FA', border: LINE_STRONG };

const pickList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.trips)) return data.trips;
    return [];
};

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const normalizeTrip = (it) => ({
    id: it.id ?? it.tripId,
    employeeCode: it.employeeCode ?? '',
    employeeName: it.employeeName ?? it.fullName ?? '',
    locationId: it.locationId ?? it.clientLocationId ?? null,
    locationName: it.locationName ?? it.clientLocationName ?? '',
    clientCompany: it.clientCompany ?? '',
    status: String(it.status ?? '').toLowerCase(),
    startedAt: it.startedAt ?? it.startTime ?? it.startedOn ?? '',
    endedAt: it.endedAt ?? it.endTime ?? it.endedOn ?? '',
    durationMinutes: num(it.durationMinutes),
    distanceMeters: num(it.distanceMeters ?? it.distanceMetres),
    pointCount: num(it.pointCount ?? it.pointsCount),
    isArchived: Boolean(it.isArchived),
});

const stamp = (v) => {
    const d = parseApiDate(v);
    if (!d) return v ? String(v) : '';
    return `${fmtDayMon(d)} · ${fmtClockTime(d)}`;
};

// One line for the whole trip window — the date once, not on both ends.
const tripWhen = (t) => {
    const s = parseApiDate(t.startedAt);
    const e = parseApiDate(t.endedAt);
    if (!s) return '—';
    if (!e) return `${fmtDayMon(s)} · ${fmtClockTime(s)}`;
    return s.toDateString() === e.toDateString()
        ? `${fmtDayMon(s)} · ${fmtClockTime(s)} → ${fmtClockTime(e)}`
        : `${fmtDayMon(s)} ${fmtClockTime(s)} → ${fmtDayMon(e)} ${fmtClockTime(e)}`;
};

// Fall back to start→end when the header carries no durationMinutes.
const durationOf = (t) => {
    let mins = t.durationMinutes;
    if (!Number.isFinite(mins)) {
        const s = parseApiDate(t.startedAt);
        const e = parseApiDate(t.endedAt);
        if (s && e) mins = (e.getTime() - s.getTime()) / 60000;
    }
    return fmtMinutes(mins);
};

const startPin = L.divIcon({
    className: 'ara-trip-pin',
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#16A34A;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.35)"></div>',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});
const endPin = L.divIcon({
    className: 'ara-trip-pin',
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#E11D48;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.35)"></div>',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

function RouteMap({ points }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);

    useEffect(() => {
        if (mapRef.current || !containerRef.current) return undefined;
        const map = L.map(containerRef.current, { center: INDIA_CENTER, zoom: 5, attributionControl: false });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        mapRef.current = map;
        const t = setTimeout(() => map.invalidateSize(), 220);

        // The panel is flex-sized, so its height changes with the viewport —
        // without this Leaflet keeps its first measurement and renders grey gaps.
        const ro = typeof ResizeObserver === 'function'
            ? new ResizeObserver(() => map.invalidateSize())
            : null;
        if (ro) ro.observe(containerRef.current);

        return () => {
            clearTimeout(t);
            if (ro) ro.disconnect();
            map.remove();
            mapRef.current = null;
            layerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }
        if (!points || points.length === 0) { map.setView(INDIA_CENTER, 5); return; }

        const group = L.featureGroup();
        L.polyline(points, { color: PRIMARY, weight: 4, opacity: 0.85, lineJoin: 'round' }).addTo(group);
        L.marker(points[0], { icon: startPin }).bindTooltip('Start').addTo(group);
        if (points.length > 1) L.marker(points[points.length - 1], { icon: endPin }).bindTooltip('End').addTo(group);
        group.addTo(map);
        layerRef.current = group;
        map.fitBounds(group.getBounds(), { padding: [28, 28], maxZoom: 17 });
    }, [points]);

    return <Box ref={containerRef} sx={{ position: 'absolute', inset: 0, bgcolor: '#EAEFF4' }} />;
}

export default function LocationTrackingPage() {
    const [trips, setTrips] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [status, setStatus] = useState('all');
    const [employeeCode, setEmployeeCode] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [locationId, setLocationId] = useState('');

    const [selected, setSelected] = useState(null);
    const [route, setRoute] = useState(null);
    const [routeLoading, setRouteLoading] = useState(false);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'error') => setSnack({ msg, sev });

    // dd-MM-yyyy is what the API takes; the native date input gives yyyy-MM-dd.
    const toApiDate = (iso) => {
        if (!iso) return undefined;
        const [y, m, d] = iso.split('-');
        return y && m && d ? `${d}-${m}-${y}` : undefined;
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetLocationTrips, {
                params: {
                    status,
                    employeeCode: employeeCode.trim() || undefined,
                    fromDate: toApiDate(fromDate),
                    toDate: toApiDate(toDate),
                    locationId: locationId || undefined,
                },
            });
            if (body?.error) throw new Error(body.message || 'Could not load trips.');
            setTrips(pickList(body?.data).map(normalizeTrip));
            setLoadError('');
        } catch (err) {
            // 404 is the documented "no trips match" response, not a failure.
            if (err?.response?.status === 404) {
                setTrips([]);
                setLoadError('');
            } else {
                setTrips([]);
                setLoadError(apiErrorMessage(err, 'Could not load trips.'));
            }
        } finally {
            setLoading(false);
        }
    }, [status, employeeCode, fromDate, toDate, locationId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let alive = true;
        http.get(GetClientLocations)
            .then(({ data: body }) => {
                if (!alive || body?.error) return;
                // Same shape the Client Locations screen reads: the list sits under
                // data.locations, and casing varies between camel and Pascal.
                const list = Array.isArray(body?.data) ? body.data : (body?.data?.locations || body?.data?.items || []);
                setLocations(list
                    .map((r) => ({
                        id: r.id ?? r.Id,
                        name: r.name ?? r.Name ?? '',
                        clientName: r.clientCompany ?? r.ClientCompany ?? r.clientName ?? r.ClientName ?? '',
                        isActive: (r.isActive ?? r.IsActive) !== false,
                    }))
                    .filter((r) => r.id != null));
            })
            .catch(() => { /* the site filter is a convenience; the grid works without it */ });
        return () => { alive = false; };
    }, []);

    const openTrip = async (t) => {
        setSelected(t);
        setRoute(null);
        setRouteLoading(true);
        try {
            const { data: body } = await http.get(GetTripRoute, { params: { tripId: t.id } });
            if (body?.error) throw new Error(body.message || 'Could not load this route.');
            const d = body?.data || {};

            // Archived trips have their raw points pruned to a polyline string.
            const archived = Boolean(d.isArchived ?? t.isArchived);
            const raw = Array.isArray(d.points) ? d.points : [];
            const points = raw.length
                ? raw
                    .slice()
                    .sort((a, b) => (num(a.sequence) ?? 0) - (num(b.sequence) ?? 0))
                    .map((p) => [num(p.latitude), num(p.longitude)])
                    .filter(([la, ln]) => la != null && ln != null)
                : decodePolyline(d.routePolyline);

            setRoute({ points, archived, simplified: !raw.length && points.length > 0 });
        } catch (err) {
            setRoute({ points: [], archived: false, simplified: false });
            notify(apiErrorMessage(err, 'Could not load this route.'));
        } finally {
            setRouteLoading(false);
        }
    };

    const clearFilters = () => {
        setStatus('all'); setEmployeeCode(''); setFromDate(''); setToDate(''); setLocationId('');
    };
    const filtered = Boolean(employeeCode || fromDate || toDate || locationId || status !== 'all');

    const counts = useMemo(() => {
        const c = { all: trips.length };
        trips.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
        return c;
    }, [trips]);

    const routeDistance = route?.points?.length > 1 ? routeLengthMetres(route.points) : 0;

    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { load, clearFilters };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {filtered && (
                    <Button onClick={() => actionsRef.current.clearFilters()} startIcon={<FilterAltOffRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...ghostBtn, height: 40, px: 1.8, fontSize: 12.5 }}>
                        Clear filters
                    </Button>
                )}
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={() => actionsRef.current.load()} disabled={loading} sx={{ ...ghostBtn, width: 40, height: 40 }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [filtered, loading, setCrumbActions]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, p: 1.6, mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                <Box sx={{ overflowX: 'auto', pb: 0.2 }}>
                    <Segmented items={STATUSES.map((s) => ({ ...s, count: counts[s.key] }))} value={status} onChange={setStatus} />
                </Box>

                {/* A grid rather than a wrapping row, so the last control can never
                    orphan onto a line of its own at a fixed width. */}
                <Box sx={{ display: 'grid', gap: 1.2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
                    <TextField
                        size="small" placeholder="Employee code" value={employeeCode}
                        onChange={(e) => setEmployeeCode(e.target.value)}
                        sx={field}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment> }}
                    />
                    <TextField
                        size="small" select label="Site" value={locationId}
                        onChange={(e) => setLocationId(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={field}
                    >
                        <MenuItem value="">All sites</MenuItem>
                        {locations.map((l) => (
                            <MenuItem key={l.id} value={l.id}>
                                <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13.5, color: INK }} noWrap>{l.name || `Site ${l.id}`}</Typography>
                                    {l.clientName && (
                                        <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>· {l.clientName}</Typography>
                                    )}
                                    {!l.isActive && (
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>(inactive)</Typography>
                                    )}
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small" type="date" label="From" value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={field}
                    />
                    <TextField
                        size="small" type="date" label="To" value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={field}
                    />
                </Box>
            </Box>

            {/* Both columns share one height so the panels line up top and bottom;
                each scrolls its own body rather than growing with its content. */}
            <Box sx={{
                display: 'grid', gap: 1.5,
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 440px) 1fr' },
                alignItems: 'stretch',
                height: { lg: 'clamp(540px, calc(100vh - 300px), 840px)' },
            }}>
                <Box sx={{ ...card, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: { xs: 380, lg: 0 } }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${LINE}`, bgcolor: '#F9F9FC', flexShrink: 0 }}>
                        <RouteRoundedIcon sx={{ fontSize: 17, color: PRIMARY }} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>Trips</Typography>
                        <Typography sx={{ fontSize: 11.5, color: MUTED, ml: 'auto' }}>
                            {loading ? 'Loading…' : `${trips.length} found`}
                        </Typography>
                    </Stack>

                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {loading ? (
                            <SkeletonRows count={5} />
                        ) : loadError ? (
                            <Box sx={{ p: 2 }}>
                                <LoadAlert sx={{ borderRadius: '9px' }}
                                    action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                                    {loadError}
                                </LoadAlert>
                            </Box>
                        ) : trips.length === 0 ? (
                            <EmptyState
                                icon={TravelExploreRoundedIcon}
                                title="No trips found"
                                hint={filtered ? 'Try widening the date range or clearing the filters.' : 'Field trips recorded by the mobile app show up here.'}
                            />
                        ) : trips.map((t, i) => {
                            const st = statusOf(t.status);
                            const on = selected?.id === t.id;
                            return (
                                <Stack
                                    key={t.id ?? i}
                                    direction="row"
                                    spacing={1.4}
                                    onClick={() => openTrip(t)}
                                    sx={{
                                        alignItems: 'flex-start', px: 2, py: 1.6, cursor: 'pointer',
                                        borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                                        borderLeft: `3px solid ${on ? PRIMARY : 'transparent'}`,
                                        bgcolor: on ? '#F6FBFE' : '#fff',
                                        transition: 'background-color .15s',
                                        '&:hover': { bgcolor: on ? '#F6FBFE' : HOVER },
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', flexShrink: 0, ...avatarTone(t.employeeName), display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px -3px ${colorFor(t.employeeName)}` }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#fff' }}>{initials(t.employeeName) || '—'}</Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, minWidth: 0 }} noWrap>
                                                {t.employeeName || t.employeeCode || 'Unknown'}
                                            </Typography>
                                            {t.isArchived && (
                                                <Tooltip arrow title="Raw points pruned — the route is a simplified path">
                                                    <ArchiveRoundedIcon sx={{ fontSize: 14, color: MUTED, flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            <Box sx={{ flex: 1 }} />
                                            <Stack
                                                direction="row" spacing={0.6}
                                                sx={{ alignItems: 'center', height: 22, px: 1, borderRadius: '999px', flexShrink: 0, bgcolor: st.bg, border: `1px solid ${st.border}` }}
                                            >
                                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: st.color }} />
                                                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</Typography>
                                            </Stack>
                                        </Stack>

                                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.25 }}>
                                            <PlaceRoundedIcon sx={{ fontSize: 13, color: '#CBD2DD', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                                                {t.locationName || 'No site'}{t.clientCompany ? ` · ${t.clientCompany}` : ''}
                                            </Typography>
                                        </Stack>

                                        <Stack direction="row" spacing={0.9} sx={{ alignItems: 'center', mt: 0.6, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: 11.5, color: BODY, fontVariantNumeric: 'tabular-nums' }}>
                                                {tripWhen(t)}
                                            </Typography>
                                            {durationOf(t) && (
                                                <Box sx={{ px: 0.8, height: 18, borderRadius: '5px', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center' }}>
                                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: BODY, fontVariantNumeric: 'tabular-nums' }}>
                                                        {durationOf(t)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ ...card, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: { xs: 460, lg: 0 } }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${LINE}`, bgcolor: '#F9F9FC', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={0.9} sx={{ alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK, minWidth: 0 }} noWrap>
                                    {selected ? (selected.employeeName || selected.employeeCode) : 'Route'}
                                </Typography>
                                {selected && (() => {
                                    const st = statusOf(selected.status);
                                    return (
                                        <Stack direction="row" spacing={0.6}
                                            sx={{ alignItems: 'center', height: 20, px: 0.9, borderRadius: '999px', flexShrink: 0, bgcolor: st.bg, border: `1px solid ${st.border}` }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: st.color }} />
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: st.color }}>{st.label}</Typography>
                                        </Stack>
                                    );
                                })()}
                            </Stack>
                            <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                                {selected ? `${selected.locationName || 'No site'} · ${tripWhen(selected)}` : 'Select a trip from the list'}
                            </Typography>
                        </Box>
                        {route?.points?.length > 0 && (
                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
                                <Chip size="small" label={`${route.points.length} points`} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#F1F5F9', color: BODY }} />
                                <Chip size="small" label={fmtDistance(routeDistance)} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#E4F0F9', color: PRIMARY }} />
                                {route.simplified && (
                                    <Tooltip arrow title="This trip was archived, so the map shows the simplified path rather than every recorded fix">
                                        <Chip size="small" icon={<ArchiveRoundedIcon sx={{ fontSize: '13px !important' }} />} label="Simplified"
                                            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#FEF6EC', color: '#B45309', '& .MuiChip-icon': { color: '#B45309' } }} />
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                    </Stack>

                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                        <RouteMap points={route?.points} />

                        {(routeLoading || !selected || (route && route.points.length === 0)) && (
                            <Box sx={{
                                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                // Above Leaflet's own controls (z-index 1000) so the
                                // zoom buttons don't float over the empty state.
                                bgcolor: 'rgba(255,255,255,0.88)', zIndex: 1200, px: 2, textAlign: 'center',
                            }}>
                                {routeLoading ? (
                                    <Stack spacing={1.2} sx={{ alignItems: 'center' }}>
                                        <CircularProgress size={26} />
                                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>Loading route…</Typography>
                                    </Stack>
                                ) : !selected ? (
                                    <Box>
                                        <RouteRoundedIcon sx={{ fontSize: 38, color: '#CBD2DD' }} />
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B', mt: 1 }}>No trip selected</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.4 }}>Pick a trip on the left to draw its route.</Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <PlaceRoundedIcon sx={{ fontSize: 38, color: '#CBD2DD' }} />
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B', mt: 1 }}>No route recorded</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.4 }}>This trip has no breadcrumb points to draw.</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>

                    {selected && route?.points?.length > 0 && (
                        <Stack direction="row" spacing={2.5} sx={{ px: 2, py: 1.4, borderTop: `1px solid ${LINE}`, flexWrap: 'wrap', gap: 1.5, flexShrink: 0 }}>
                            <Box>
                                <Label>Started</Label>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{stamp(selected.startedAt) || '—'}</Typography>
                            </Box>
                            <Box>
                                <Label>Ended</Label>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{stamp(selected.endedAt) || '—'}</Typography>
                            </Box>
                            <Box>
                                <Label>Duration</Label>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{durationOf(selected)}</Typography>
                            </Box>
                            <Box>
                                <Label>Distance</Label>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtDistance(selected.distanceMeters ?? routeDistance)}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Box>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'error'} variant="filled" sx={{ borderRadius: RADIUS }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
